import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faSave } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createAccountMessageTemplate } from 'api/engage';
import * as DropdownMenu from 'components/dropdown-menu';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageMessage, EngageMessageChannel, EngageMessageTemplate } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type EngageTemplateAttributes = Partial<
    Pick<EngageMessage, 'body' | 'internal_name' | 'subject' | 'body_json' | 'editor'>
>;
interface SaveAsTemplateActionProps {
    values: EngageTemplateAttributes;
    channel: EngageMessageChannel;
    disabled?: boolean;
}
const SaveAsTemplateAction = ({ values, channel, disabled = false }: SaveAsTemplateActionProps) => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    const { mutate: createMessageTemplate } = useMutation<
        EngageMessageTemplate | null,
        GivebutterAxiosError
    >({
        mutationFn: () => {
            const subject = channel === 'email' ? values.subject : undefined;
            return createAccountMessageTemplate(accountId, {
                name: values.internal_name ?? '',
                body: values.body ?? '',
                body_json: values.body_json,
                editor: values.editor,
                subject,
                channel,
            });
        },
        onSuccess: () => {
            success('Template created successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageAccountMessageTemplates(accountId),
            });
        },
        onError: e => {
            error(e.response?.data.message || 'Failed to create template. Please try again.');
        },
    });

    return (
        <DropdownMenu.Item
            onSelect={() => createMessageTemplate()}
            onClick={e => e.stopPropagation()}
            className="flex items-center gap-2"
            disabled={disabled}
        >
            <FontAwesomeIcon icon={faSave} />
            Save as template
        </DropdownMenu.Item>
    );
};

export { SaveAsTemplateAction };
